import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import {
  type IntegrationConnectionType,
  type IntegrationProviderType,
  mutateIntegrationConnectionDelete,
} from '@/api/integration';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { Text } from '@/components/typography';
import SolAPIError from '@/error/SolAPIError';

type Props = {
  open: boolean;
  provider: IntegrationProviderType;
  connection: IntegrationConnectionType;
  onOk: () => void;
  onCancel: () => void;
};

const DisconnectConnectionDialog = ({ open, provider, connection, onOk, onCancel }: Props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<unknown | null>(null);

  const getErrorMessage = (unknownError: unknown) => {
    if (unknownError instanceof SolAPIError) return unknownError.getFirstError().message;
    if (unknownError instanceof Error) return unknownError.message;
    return null;
  };

  const handleDisconnect = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await mutateIntegrationConnectionDelete(connection.id!);
      onOk();
    } catch (e) {
      setError(e);
    } finally {
      setIsSaving(false);
    }
  };

  const errorMessage = getErrorMessage(error);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isSaving) return;
        if (!isOpen) onCancel();
      }}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) setError(null);
      }}
    >
      <DialogContent showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            <Trans>Disconnect Connection</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div>
            <Trans>
              Do you really want to disconnect{' '}
              <strong>{connection.createdBy?.email ?? 'this account'}</strong>? {provider.name} data
              will not be available if you continue.
            </Trans>
          </div>
          {errorMessage && (
            <Text variant="body2" color="red">
              {errorMessage}
            </Text>
          )}
        </div>
        <DialogFooter>
          <DialogClose
            render={
              <Button size="large" disabled={isSaving}>
                <Trans>Cancel</Trans>
              </Button>
            }
          />
          <Button
            size="large"
            variant="primary"
            danger
            isLoading={isSaving}
            onClick={handleDisconnect}
          >
            <Trans>Yes, Disconnect</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisconnectConnectionDialog;
